import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { useUpdateEstimationDetails } from "@/hooks/mutation/estimates/useUpdateEstimationDetails";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

interface ChangeDefaultNameAlertProps {
  open: boolean;
  estimationId: string;
  onExit: () => void;
  onClose: () => void;
}

export const ChangeDefaultNameAlert = ({ open, onExit, onClose, estimationId }: ChangeDefaultNameAlertProps) => {
  const { estimation } = useEstimateDetail(estimationId);
  const { updateEstimationDetails } = useUpdateEstimationDetails(estimationId);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[480px] gap-2 rounded-xl border border-gray-200 bg-white p-6 shadow-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-left text-xl font-semibold">You haven’t given this project a name.</DialogTitle>
          <DialogDescription className="text-left text-sm !text-gray-500">
            Are you sure you want to exit without saving it?
          </DialogDescription>
        </DialogHeader>

        <div>
          <UpdateProjectNameInput
            label="Project name"
            placeholder="Enter a project name"
            value={estimation?.projectName || ""}
            onBlur={(newValue) => {
              if (!estimation) return;
              const updated = { ...estimation, projectName: newValue };
              updateEstimationDetails(updated, "projectName");
            }}
          />
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onExit}>Return to dashboard</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface UpdateProjectNameInputProps {
  label: string;
  value: string;
  placeholder?: string;
  onBlur: (newValue: string) => void;
}

const UpdateProjectNameInput = ({ label, value, placeholder, onBlur }: UpdateProjectNameInputProps) => {
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleBlur = () => {
    if (internalValue !== value && internalValue.trim().length > 0) {
      onBlur(internalValue);
    }
  };

  return (
    <div className="w-full space-y-1">
      <label className="text-xs font-medium text-[#71717A]">{label}</label>
      <Input
        value={internalValue}
        onChange={(e) => setInternalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="max-h-7 w-full max-w-[260px] rounded-lg !border-gray-300 text-sm"
      />
    </div>
  );
};
